import api from './index'
import type {
  ApiResponse,
  FormInstanceDetail,
  FormInstanceStatus,
  FormAssignmentRole,
  FormSubmissionStatus,
} from '@/types'

export const listInstances = async (
  projectId: string,
): Promise<ApiResponse<{ instances: FormInstanceDetail[] }>> => {
  const response = await api.get(`/forms/instances/project/${projectId}`)
  return {
    success: response.data?.success ?? true,
    data: { instances: response.data?.data?.instances ?? [] },
    message: response.data?.message,
  }
}

export const createInstance = async (
  payload: Partial<FormInstanceDetail> & { templateId: string; title: string; assignments?: Array<{ userId: string; role?: FormAssignmentRole }> }
): Promise<ApiResponse<{ instance: FormInstanceDetail }>> => {
  const response = await api.post('/forms/instances', payload)
  return {
    success: response.data?.success ?? true,
    data: { instance: response.data?.data },
    message: response.data?.message,
  }
}

export const updateInstance = async (
  instanceId: string,
  payload: Partial<Pick<FormInstanceDetail, 'title' | 'status' | 'dueDate' | 'instructions' | 'metadata'>> & {
    status?: FormInstanceStatus
  }
): Promise<ApiResponse<{ instance: FormInstanceDetail }>> => {
  const response = await api.patch(`/forms/instances/${instanceId}`, payload)
  return {
    success: response.data?.success ?? true,
    data: { instance: response.data?.data },
    message: response.data?.message,
  }
}

export const updateAssignments = async (
  instanceId: string,
  assignments: Array<{ userId: string; role?: FormAssignmentRole }>
): Promise<ApiResponse> => {
  const response = await api.post(`/forms/instances/${instanceId}/assignments`, { assignments })
  return response.data
}

export const deleteInstance = async (
  instanceId: string,
): Promise<ApiResponse> => {
  const response = await api.delete(`/forms/instances/${instanceId}`)
  return response.data
}

export const submitInstance = async (
  instanceId: string,
  payload: {
    status?: FormSubmissionStatus
    reviewNotes?: string | null
    metadata?: Record<string, unknown>
    location?: Record<string, unknown>
    values?: Array<{
      fieldId: string
      valueString?: string | null
      valueNumber?: string | number | null
      valueBoolean?: boolean | null
      valueDate?: string | null
      valueJson?: unknown
      anomalyLevel?: string | null
    }>
  }
): Promise<ApiResponse> => {
  const response = await api.post(`/forms/instances/${instanceId}/submit`, payload)
  return response.data
}

export const reviewSubmission = async (
  instanceId: string,
  submissionId: string,
  payload: { action: 'approve' | 'reject' | 'request_changes'; notes?: string | null }
): Promise<ApiResponse> => {
  const response = await api.post(`/forms/instances/${instanceId}/submissions/${submissionId}/review`, payload)
  return response.data
}

export const getInstanceDetail = async (
  instanceId: string,
): Promise<ApiResponse<FormInstanceDetail>> => {
  const response = await api.get(`/forms/instances/${instanceId}`)
  return {
    success: response.data?.success ?? true,
    data: response.data?.data,
    message: response.data?.message,
  }
}

export const toggleInstanceLock = async (
  instanceId: string,
  locked: boolean,
): Promise<ApiResponse<{ locked: boolean }>> => {
  const response = await api.patch(`/forms/instances/${instanceId}/lock`, { locked })
  return response.data
}
